import { queryClient } from "@/src/config/react-query";
import { MembershipStatus } from "@/src/constants/groups/group-membership";
import {
  JoinRequestPayload,
  NotificationItem,
  NotificationPayload,
} from "@/src/models/notification.model";
import {
  useRemoveGroupMembership,
  useUpdateGroupMembership,
} from "@/src/services/api/groups/groups.hooks";
import { useMarkNotificationAsRead } from "@/src/services/api/notification/notification.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { useRouter } from "expo-router";
import { More } from "iconsax-react-nativejs";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";

export const getRelativeTime = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSecs < 60) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays} days ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const showsActionButtons = (type: string) =>
  type === "join_request" || type === "event_invite";

const getNavigationTarget = (
  parsedPayload: NotificationPayload | null,
  router: ReturnType<typeof useRouter>,
) => {
  if (
    !parsedPayload ||
    !("targetEntityId" in parsedPayload) ||
    !parsedPayload.targetEntityId
  )
    return;
  switch (parsedPayload.type) {
    case "join_request":
      router.push({
        pathname: "/groups/[id]",
        params: {
          id: parsedPayload.targetEntityId,
          tab: "members",
          membersTab: "pending",
        },
      });
      break;
    case "added_to_group":
      router.push({
        pathname: "/groups/[id]",
        params: { id: parsedPayload.targetEntityId },
      });
      break;
    case "post_reaction":
    case "post_comment":
    case "post_share":
      router.push({
        pathname: "/feeds",
        params: { post_id: parsedPayload.targetEntityId },
      });
      break;
    case "event_created":
    case "event_invite":
      router.push({
        pathname: "/events/[id]",
        params: { id: parsedPayload.targetEntityId },
      });
      break;
    case "wiki_article_created":
      router.push({
        pathname: "/(tabs)/wiki/wiki-article-detail",
        params: { articleId: parsedPayload.targetEntityId },
      });
      break;
    case "wiki_article_updated":
      router.push({
        pathname: "/(tabs)/wiki/wiki-article-detail",
        params: { articleId: parsedPayload.targetEntityId },
      });
      break;
    case "news_article_created":
      router.push({
        pathname: "/(tabs)/news/news-detail",
        params: { newsId: parsedPayload.targetEntityId },
      });
      break;
    case "news_article_updated":
      router.push({
        pathname: "/(tabs)/news/news-detail",
        params: { newsId: parsedPayload.targetEntityId },
      });
      break;
    default:
      break;
  }
};

const ActionButtons = ({
  onAccept,
  onDecline,
  isPending,
}: {
  onAccept: () => void;
  onDecline: () => void;
  isPending: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <View className="flex-row gap-2 mt-2">
      {isPending ? (
        <ActivityIndicator size="small" />
      ) : (
        <>
          <TouchableOpacity
            className="bg-primary px-5 py-1.5 rounded-full hover:opacity-90"
            onPress={onAccept}
          >
            <Text className="text-chalk text-xs font-medium">
              {t("LABEL_ACCEPT")}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            className="border border-primary px-5 py-1.5 rounded-full hover:bg-ash-3"
            onPress={onDecline}
          >
            <Text className="text-primary text-xs font-medium">
              {t("LABEL_DECLINE")}
            </Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};

export const NotificationRow = ({ item }: { item: NotificationItem }) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;
  const router = useRouter();
  const [openMenu, setOpenMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [actionPending, setActionPending] = useState(false);
  const moreButtonRef = useRef<View>(null);

  let parsedPayload: NotificationPayload | null = null;
  try {
    parsedPayload = JSON.parse(item.denormalizedPayload as unknown as string);
  } catch (e) {
    console.warn("Failed to parse notification payload:", e);
  }

  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const { mutateAsync: updateGroupMembership } = useUpdateGroupMembership();
  const { mutateAsync: removeGroupMembership } = useRemoveGroupMembership();

  const handleMorePress = (e: any) => {
    e.stopPropagation();
    moreButtonRef.current?.measure((_fx, _fy, width, height, px, py) => {
      setMenuPosition({ x: px - 160 + width, y: py + height });
      setOpenMenu(true);
    });
  };

  const handleAccept = async () => {
    if (!parsedPayload || parsedPayload.type !== "join_request") return;
    const payload = parsedPayload as JoinRequestPayload;
    if (!payload.membershipId) return;
    setActionPending(true);
    try {
      await updateGroupMembership({
        filter: JSON.stringify({ ItemId: payload.membershipId }),
        status: MembershipStatus.ACTIVE,
      });
      queryClient.invalidateQueries({ queryKey: ["getGroupMembers"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount"] });
      markAsRead(item.id);
    } catch (e) {
      console.error(e);
    } finally {
      setActionPending(false);
    }
  };

  const handleDecline = async () => {
    if (!parsedPayload || parsedPayload.type !== "join_request") return;
    const payload = parsedPayload as JoinRequestPayload;
    if (!payload.membershipId) return;
    setActionPending(true);
    try {
      await removeGroupMembership(payload.membershipId);
      queryClient.invalidateQueries({ queryKey: ["getGroupMembers"] });
      queryClient.invalidateQueries({ queryKey: ["getGroupMemberCount"] });
      markAsRead(item.id);
    } catch (e) {
      console.error(e);
    } finally {
      setActionPending(false);
    }
  };

  const actorAvatar =
    parsedPayload && "actorAvatar" in parsedPayload
      ? parsedPayload.actorAvatar
      : undefined;
  const actorName =
    parsedPayload && "actorName" in parsedPayload
      ? (parsedPayload.actorName ?? "")
      : "";

  const isUnread = !item.isRead;

  const renderAvatar = (size: number) =>
    actorAvatar ? (
      <Image
        source={{ uri: actorAvatar }}
        style={{ width: size, height: size, borderRadius: size / 2 }}
      />
    ) : (
      <View
        style={{ width: size, height: size, borderRadius: size / 2 }}
        className="bg-ash items-center justify-center"
      >
        <Text className="text-slate font-medium text-base">
          {actorName?.charAt(0)?.toUpperCase() ?? "?"}
        </Text>
      </View>
    );

  const moreButton = (
    <View ref={moreButtonRef}>
      <Pressable
        onPress={handleMorePress}
        hitSlop={8}
        className="p-1 active:opacity-70 hover:bg-ash-3 hover:rounded-full"
      >
        <More size={16} color={themeVars["--color-ash-1"]} rotation={90} />
      </Pressable>
    </View>
  );

  const actionButtons = showsActionButtons(parsedPayload?.type ?? "") && (
    <ActionButtons
      onAccept={handleAccept}
      onDecline={handleDecline}
      isPending={actionPending}
    />
  );

  return (
    <>
      {isMobile ? (
        // Modern feed row: borderless, edge-to-edge, unread = subtle tint + dot.
        <Pressable
          style={varStyle}
          onPress={() => getNavigationTarget(parsedPayload, router)}
          className={`flex-row gap-3 px-3 py-3 rounded-2xl active:bg-ash-3 hover:bg-ash-3 ${
            isUnread ? "bg-primary-2/60" : ""
          }`}
        >
          {renderAvatar(48)}

          <View className="flex-1">
            <Text className="text-sm text-onyx leading-5" numberOfLines={3}>
              {parsedPayload?.message}
            </Text>
            <Text
              className={`text-xs mt-1 ${
                isUnread ? "text-primary font-medium" : "text-ash-1"
              }`}
            >
              {getRelativeTime(item.createdTime)}
            </Text>
            {actionButtons}
          </View>

          <View className="items-center gap-3 pl-1">
            {moreButton}
            {isUnread && (
              <View className="w-2.5 h-2.5 rounded-full bg-primary" />
            )}
          </View>
        </Pressable>
      ) : (
        <Pressable
          style={varStyle}
          onPress={() => getNavigationTarget(parsedPayload, router)}
          className="bg-chalk mb-2 rounded-2xl border border-ash-3 active:bg-ash-3 web:hover:bg-ash-3"
        >
          <View className="flex-row items-center px-4 py-2">
            <View className="mr-3">{renderAvatar(40)}</View>

            <View className="flex-1">
              <Text className="text-sm text-onyx leading-5" numberOfLines={2}>
                {parsedPayload?.message}
              </Text>
              {actionButtons}
            </View>

            <View className="items-end ml-3 gap-1">
              <Text className="text-xs text-ash-1">
                {getRelativeTime(item.createdTime)}
              </Text>
              {moreButton}
            </View>
          </View>
        </Pressable>
      )}

      <Modal
        visible={openMenu}
        transparent
        animationType="none"
        onRequestClose={() => setOpenMenu(false)}
      >
        <Pressable style={{ flex: 1 }} onPress={() => setOpenMenu(false)}>
          <View
            style={{
              position: "absolute",
              top: menuPosition.y,
              left: menuPosition.x,
              backgroundColor: themeVars["--color-chalk-default"],
              borderWidth: 1,
              borderColor: themeVars["--color-ash-3"],
              borderRadius: 12,
              minWidth: 160,
              overflow: "hidden",
              elevation: 20,
              shadowColor: themeVars["--color-shadow-dark"],
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 1,
              shadowRadius: 8,
            }}
          >
            {!item.isRead && (
              <TouchableOpacity
                onPress={() => {
                  markAsRead(item.id);
                  setOpenMenu(false);
                }}
                className="hover:bg-ash-3"
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  borderBottomWidth: 1,
                  borderBottomColor: themeVars["--color-ash-3"],
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    color: themeVars["--color-onyx-default"],
                  }}
                >
                  {t("BTN_MARK_AS_READ")}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              onPress={() => setOpenMenu(false)}
              className="hover:bg-ash-3"
              style={{ paddingHorizontal: 16, paddingVertical: 12 }}
            >
              <Text
                style={{
                  fontSize: 14,
                  color: themeVars["--color-crimson-default"],
                }}
              >
                {t("LABEL_DELETE")}
              </Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </>
  );
};
